import { createContext, useContext, useRef, type ReactNode } from "react";
import { createStore, useStore } from "zustand";

/**
 * Court-sheet shopping cart — CLIENT-SIDE selection state ONLY.
 *
 * The cart is the set of open 1-hr slots a player intends to book, accumulated
 * across courts AND days. It has NO endpoint and moves NO money (plan.scope_3a):
 * the subtotal is an ESTIMATE (sum of per-slot `estimate`), never a binding
 * price. The binding quote + charge are a 3b concern.
 *
 * A cart item is the honest, minimal record of a chosen slot. Identity is
 * (court_id, start_time): the SAME court+hour on two different days are two
 * distinct items (the start_time carries the date), and re-adding the same slot
 * is idempotent (no duplicate).
 *
 * State lives in a Zustand store (REBUILD-SPEC §4: Zustand for minimal UI-only
 * client state). The store is NOT a global singleton — `CartProvider` creates a
 * fresh store per booking flow and hands it down via context, so there is no
 * stray cross-route cart. `useCart()` outside `CartProvider` THROWS.
 */

export interface CartSlot {
  facility_id: string;
  court_id: string;
  court_name: string;
  start_time: string;
  end_time: string;
  /** Per-slot dollar estimate, or null when the facility has no pricing rule. */
  estimate: number | null;
  /**
   * ISO-4217 currency of the estimate (e.g. "USD", "CAD"), threaded so a non-USD
   * facility is never formatted as bare US dollars. Optional; consumers default
   * to "USD" via `formatMoney` when absent.
   */
  currency?: string;
}

export interface CartContextValue {
  items: CartSlot[];
  count: number;
  /** Sum of per-slot estimates; null estimates contribute $0 (never invented). */
  totalEstimate: number;
  add: (slot: CartSlot) => void;
  remove: (slot: Pick<CartSlot, "court_id" | "start_time">) => void;
  toggle: (slot: CartSlot) => void;
  clear: () => void;
  /** True iff this exact (court, start_time) is currently in the cart. */
  has: (slot: Pick<CartSlot, "court_id" | "start_time">) => boolean;
}

/** A slot's identity = (court, start_time). Same hour, different day → distinct. */
function sameSlot(
  a: Pick<CartSlot, "court_id" | "start_time">,
  b: Pick<CartSlot, "court_id" | "start_time">,
): boolean {
  return a.court_id === b.court_id && a.start_time === b.start_time;
}

interface CartStoreState {
  items: CartSlot[];
  add: (slot: CartSlot) => void;
  remove: (slot: Pick<CartSlot, "court_id" | "start_time">) => void;
  toggle: (slot: CartSlot) => void;
  clear: () => void;
}

type CartStoreApi = ReturnType<typeof createCartStore>;

function createCartStore() {
  return createStore<CartStoreState>((set) => ({
    items: [],
    add: (slot) =>
      set((s) =>
        // Idempotent — re-adding the same (court, start_time) is a no-op.
        s.items.some((it) => sameSlot(it, slot))
          ? s
          : { items: [...s.items, slot] },
      ),
    remove: (slot) =>
      set((s) => ({ items: s.items.filter((it) => !sameSlot(it, slot)) })),
    toggle: (slot) =>
      set((s) =>
        s.items.some((it) => sameSlot(it, slot))
          ? { items: s.items.filter((it) => !sameSlot(it, slot)) }
          : { items: [...s.items, slot] },
      ),
    clear: () => set({ items: [] }),
  }));
}

const CartStoreContext = createContext<CartStoreApi | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  // One store instance per provider mount, scoped to this booking flow.
  const storeRef = useRef<CartStoreApi | null>(null);
  storeRef.current ??= createCartStore();
  return (
    <CartStoreContext.Provider value={storeRef.current}>
      {children}
    </CartStoreContext.Provider>
  );
}

/**
 * Provides a cart ONLY if one is not already in context.
 *
 * The cart is normally provided ONCE at BookingLayout (lifted to wrap the whole
 * /book/:facilityId subtree — sheet + review). But the court sheet is also
 * rendered standalone (e.g. in isolation tests). This boundary lets the page
 * self-provide a cart when mounted alone WITHOUT creating a second, stray store
 * when nested under the lifted provider — there is exactly one authoritative
 * store at runtime (the lifted one), so the sheet's selection survives into
 * review. No dual cart path.
 */
export function CartBoundary({ children }: { children: ReactNode }) {
  const existing = useContext(CartStoreContext);
  if (existing !== null) return <>{children}</>;
  return <CartProvider>{children}</CartProvider>;
}

export function useCart(): CartContextValue {
  const store = useContext(CartStoreContext);
  if (store === null) {
    throw new Error("useCart must be used within a <CartProvider>");
  }
  const items = useStore(store, (s) => s.items);
  const add = useStore(store, (s) => s.add);
  const remove = useStore(store, (s) => s.remove);
  const toggle = useStore(store, (s) => s.toggle);
  const clear = useStore(store, (s) => s.clear);

  const totalEstimate = items.reduce((sum, it) => sum + (it.estimate ?? 0), 0);

  return {
    items,
    count: items.length,
    totalEstimate,
    add,
    remove,
    toggle,
    clear,
    has: (slot) => items.some((it) => sameSlot(it, slot)),
  };
}
